/**
 * Las rutas que una cuenta del plan Drop puede abrir: UNA lista blanca, la misma idea que el menú.
 *
 * 🚨 EL FOUNDER LO PREGUNTÓ EXACTO (21-sep-2026): «¿si el drop conoce la ruta de los módulos puede
 *    acceder a todo?». Podía. El menú era una lista blanca (`MENU_DEL_ROL_DROP`), pero las rutas
 *    se cuidaban por MÓDULO (`FeatureProtectedRoute feature=…`) y 150 de 282 no declaran ninguno:
 *    Automatización, Ads, Contabilidad, Reportes, Estadísticas, Marketing, POS, Inventario,
 *    Variantes, Estudio de foto… Un dropshipper con la URL entraba a todo eso en producción.
 *
 * ⭐ LA REGLA ES DEL PLAN Y VIVE EN UN SOLO LUGAR. Esconder módulos uno por uno deja pasar el que
 *    se agregue mañana; con lista blanca, lo nuevo queda afuera solo. `PortonDelRolDrop` la aplica
 *    una sola vez, arriba de todas las rutas del panel, y NO se apaga en desarrollo: el rol es un
 *    dato, y verlo con todo abierto no es verlo.
 *
 * 🪤 Lo que la base ya protege con RLS no depende de esto: esto es la puerta de la PANTALLA.
 */
import { matchPath } from 'react-router-dom';
import { ROUTES } from '@/config/routes.config';

export const RUTAS_DEL_ROL_DROP: readonly string[] = [
  // 🪤 Sin la raíz de la tienda (`/store/:slug`): es el tablero de una TIENDA, y a un Drop lo manda acá
  //    mismo a `/home`, donde el plan dibuja SU Inicio (InicioSegunElPlan).
  ROUTES.HOME,
  // Las novedades de Smarty: el muro donde lee lo que se publicó para él (plan de novedades).
  ROUTES.NOVEDADES,
  // Todas las notificaciones (20-oct-2026): la campana muestra las últimas 20 y su pie lleva acá.
  ROUTES.NOTIFICACIONES,
  // Ventas: las mismas colas que en los planes de pago, y la venta con sus páginas (sin Documentos: no factura)
  ROUTES.OPS_VENTAS, ROUTES.OPS_PEDIDOS, ROUTES.OPS_COBRANZAS, ROUTES.OPS_CANCELADAS, ROUTES.OPS_ORDER_NEW,
  ROUTES.OPS_ORDER_DETAIL, ROUTES.OPS_ORDER_ENTREGA, ROUTES.OPS_ORDER_DROP, ROUTES.OPS_ORDER_PAGOS,
  // La configuración de Ventas: dar por cobrada la venta al entregarse (20-oct-2026). La cambian el dueño o el gerente.
  ROUTES.OPS_SETTINGS,
  /*
   * ⭐ SÓLO LA FICHA DEL PRODUCTO, NO EL MÓDULO (24-sep-2026). La lista de «Mis productos» pasó a
   *    ser la pestaña «Míos» del catálogo: lo que trajo se mira donde lo eligió. Y con la lista se
   *    fueron Categorías y Etiquetas, que estaban acá abiertas por URL aunque el menú no las
   *    mostrara —el submenú de este rol está vacío desde el 21-sep— para un rol que **no tiene
   *    tienda propia** donde usarlas. Superficie que nadie abre pero cualquiera puede abrir.
   *
   * 🪤 LA FICHA SE QUEDA, y es imprescindible: es donde cambia fotos, nombre y descripción, y la
   *    pestaña «Míos» enlaza a ella. Sacarla dejaría la pestaña sin fondo.
   *
   * 🪤 SIN CREAR: el producto se importa, no se inventa.
   */
  ROUTES.PRODUCTS_EDIT,
  // Drop: la bienvenida (su Inicio), el catálogo, la Billetera, sus compras y la liquidación de sus
  // ventas («una cosa es la billetera y otra la liquidación», founder, 25-sep)
  ROUTES.DROPSHIPPING_SMARTYDROP, ROUTES.DROPSHIPPING_SMARTYDROP_BIENVENIDA,
  ROUTES.DROPSHIPPING_SMARTYDROP_SALDO, ROUTES.DROPSHIPPING_SMARTYDROP_PEDIDOS,
  ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACION,
  // Transporte como en los planes de pago: agencias y motorizados, con sus páginas. Sus couriers de Lima
  // se invitan arriba de Motorizados (founder, 25-sep: «en vez de courier, el módulo de transporte»).
  ROUTES.TRANSPORT_AGENCIES, ROUTES.TRANSPORT_AGENCY_COURIER_DETAIL,
  ROUTES.TRANSPORT_MOTORIZADOS, ROUTES.TRANSPORT_MOTOR_COURIER_DETAIL,
  // Clientes
  ROUTES.CUSTOMERS, ROUTES.CUSTOMER_DETAIL,
  // Ajustes: lo mismo que su menú, y las integraciones de su lista blanca (ver INTEGRACIONES_DEL_PLAN_DROP)
  ROUTES.SETTINGS, ROUTES.SETTINGS_GENERAL, ROUTES.SETTINGS_BUSINESS,
  ROUTES.SETTINGS_USERS, ROUTES.SETTINGS_USERS_ROLES, ROUTES.SETTINGS_USERS_SECURITY,
  ROUTES.SETTINGS_INTEGRATIONS, ROUTES.SETTINGS_INTEGRATIONS_SHOPIFY, ROUTES.SETTINGS_INTEGRATIONS_SHALOM, ROUTES.SETTINGS_INTEGRATIONS_OLVA,
  ROUTES.SETTINGS_PLAN,
];

/** A dónde va una cuenta Drop que abre una ruta que no es suya: a su Inicio (`/home`, que el plan dibuja como suyo). */
export const A_DONDE_VA_EL_DROP = ROUTES.HOME;

/** Quita el prefijo `/store/:slug` y deja la ruta como la conoce `ROUTES`. */
export function rutaSinTienda(pathname: string): string {
  const m = matchPath({ path: '/store/:slug/*' }, pathname) ?? matchPath({ path: '/store/:slug' }, pathname);
  if (!m) return pathname;
  const resto = (m.params as { '*'?: string })['*'] ?? '';
  return resto ? `/${resto}` : '/';
}

/** ¿Puede una cuenta del plan Drop abrir esta URL? Compara contra la lista blanca, con parámetros (`:id`). */
export function laRutaEsDelRolDrop(pathname: string): boolean {
  const ruta = rutaSinTienda(pathname);
  return RUTAS_DEL_ROL_DROP.some((patron) => matchPath({ path: patron, end: true }, ruta) !== null);
}
